/**
 * Moon & Jade - Chinese Zodiac Bracelets Shopify Theme
 * JavaScript Modules: Cart, Filters, Animations, Lazy Loading, 360° Viewer
 * @version 1.0.0
 */

(function () {
  'use strict';

  /* ====================================================================
     Utility Helpers
     ==================================================================== */

  /**
   * Debounce function for performance optimization
   * @param {Function} fn - Function to debounce
   * @param {number} delay - Delay in ms (default: 100)
   */
  function debounce(fn, delay = 100) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /**
   * Format price with currency
   * @param {number} amount - Price in cents
   * @param {string} currency - Currency code (default: 'USD')
   */
  function formatPrice(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100);
  }

  /**
   * Show toast notification
   * @param {string} message - Message to display
   * @param {string} type - 'success' | 'error' | 'info'
   */
  function showToast(message, type = 'success') {
    const toast = document.getElementById('toast');
    toast.textContent = message;
    toast.style.background = type === 'error' ? 'var(--color-error)' :
      type === 'info' ? 'var(--color-info)' : 'var(--color-dark)';
    toast.classList.add('toast--visible');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => toast.classList.remove('toast--visible'), 3000);
  }

  /**
   * Add event delegation helper
   */
  function delegate(parent, eventType, selector, handler) {
    parent.addEventListener(eventType, function (e) {
      const target = e.target.closest(selector);
      if (target && parent.contains(target)) {
        handler.call(target, e);
      }
    });
  }

  /* ====================================================================
     Cart Module
     ==================================================================== */

  const CartModule = {
    cart: null,     // cached cart state from /cart.js
    drawer: null,
    overlay: null,
    _itemsEl: null,
    _footerEl: null,
    _isRendering: false,
    _isUpdating: false,  // debounce guard for qty change operations

    init() {
      this.drawer = document.getElementById('cart-drawer');
      this.overlay = document.getElementById('cart-overlay');

      // Cache child containers for partial re-render
      this._itemsEl = this.drawer?.querySelector('#cart-drawer-items');
      this._footerEl = this.drawer?.querySelector('#cart-drawer-footer');

      // Cart toggle is handled by the inline onclick="toggleCart()" in header.liquid
      // and the global window.toggleCart bridge. No duplicate addEventListener here.

      // Overlay click closes the cart drawer
      document.getElementById('cart-overlay')?.addEventListener('click', () => this.close());

      // Add to cart delegation (product cards)
      delegate(document.body, 'click', '[data-add-to-cart]', function (e) {
        e.preventDefault();
        e.stopPropagation(); // prevent click from bubbling to parent <a> link
        const id = this.dataset.productId;
        const name = this.dataset.productName;
        const price = this.dataset.productPrice;
        // Show loading spinner on the clicked button
        this.classList.add('card__add-btn--loading');
        this.disabled = true;
        CartModule.addItem(id, 1, { name, price }).finally(() => {
          this.classList.remove('card__add-btn--loading');
          this.disabled = false;
        });
      });

      // Cart drawer internal actions — delegated on the drawer element
      if (this.drawer) {
        // Quantity + / - buttons
        delegate(this.drawer, 'click', '[data-cart-qty]', function (e) {
          e.preventDefault();
          // Prevent rapid double-clicks during update
          if (CartModule._isUpdating) return;
          const line = parseInt(this.dataset.cartQty);
          const delta = parseInt(this.dataset.cartDelta || '0');
          const btn = this;
          btn.classList.add('cart-item__qty-btn--loading');
          btn.disabled = true;
          CartModule._handleQtyChange(line, delta).finally(() => {
            btn.classList.remove('cart-item__qty-btn--loading');
            btn.disabled = false;
          });
        });

        // Remove button
        delegate(this.drawer, 'click', '[data-cart-remove]', function (e) {
          e.preventDefault();
          if (CartModule._isUpdating) return;
          const line = parseInt(this.dataset.cartRemove);
          CartModule._handleRemove(line, this.closest('.cart-item'));
        });
      }
    },

    toggle() {
      if (this.drawer.classList.contains('cart-drawer--open')) {
        this.close();
      } else {
        this.open();
      }
    },

    open() {
      this.drawer?.classList.add('cart-drawer--open');
      this.overlay?.classList.add('overlay--visible');
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.drawer?.classList.remove('cart-drawer--open');
      this.overlay?.classList.remove('overlay--visible');
      document.body.style.overflow = '';
    },

    /**
     * Add item to cart via Shopify AJAX API.
     * Re-renders the drawer content with fresh cart data, then opens.
     */
    async addItem(variantId, quantity = 1, productInfo = {}) {
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: quantity })
        });
        await this.render();
        this.open();
        showToast(`✓ ${productInfo.name || 'Item'} added to cart`);
        // Track event if analytics available
        if (typeof gtag !== 'undefined') {
          gtag('event', 'add_to_cart', {
            items: [{ id: variantId, name: productInfo.name, price: productInfo.price }]
          });
        }
      } catch (error) {
        showToast('Failed to add item. Please try again.', 'error');
      }
    },

    /**
     * Update cart item quantity via Shopify AJAX.
     * Re-renders the drawer without page reload.
     */
    async updateItem(lineIndex, quantity) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: quantity })
        });
        await this.render();
      } catch (error) {
        showToast('Failed to update cart.', 'error');
      }
    },

    /**
     * Remove item from cart by setting quantity to 0.
     * Re-renders the drawer without page reload.
     */
    async removeItem(lineIndex) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: 0 })
        });
        await this.render();
      } catch (error) {
        showToast('Failed to remove item.', 'error');
      }
    },

    /**
     * Fetch current cart state and re-render the entire drawer UI.
     * Re-queries #cart-drawer-items / #cart-drawer-footer on every call
     * to guarantee the containers exist (handles late DOM insertion).
     */
    async render() {
      if (this._isRendering) return;
      this._isRendering = true;
      try {
        // Show a subtle loading state in the items area
        if (this._itemsEl) {
          this._itemsEl.style.opacity = '0.5';
        }
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error(`Cart API returned ${response.status}`);
        this.cart = await response.json();
        // Re-query containers fresh every render — belt-and-suspenders against stale refs
        if (!this.drawer) this.drawer = document.getElementById('cart-drawer');
        this._itemsEl = this.drawer?.querySelector('#cart-drawer-items');
        this._footerEl = this.drawer?.querySelector('#cart-drawer-footer');
        this._updateCountBadge(this.cart.item_count);
        this._renderItems(this.cart);
        this._renderFooter(this.cart);
      } catch (error) {
        console.error('[CartModule] render failed:', error);
        showToast('Unable to load cart. Please try again.', 'error');
      } finally {
        if (this._itemsEl) this._itemsEl.style.opacity = '';
        this._isRendering = false;
      }
    },

    /**
     * Update cart count badge in the header.
     */
    _updateCountBadge(count) {
      const countEl = document.getElementById('cart-count');
      if (countEl) {
        countEl.textContent = count;
        countEl.dataset.cartCount = count;
        // Animate badge
        countEl.classList.remove('cart-count--bump');
        void countEl.offsetWidth; // force reflow
        countEl.classList.add('cart-count--bump');
      }
    },

    /**
     * Build cart items HTML from cart object and inject into drawer.
     */
    _renderItems(cart) {
      if (!this._itemsEl) return;

      if (!cart.items || cart.items.length === 0) {
        this._itemsEl.innerHTML = `
          <div class="cart-drawer__empty">
            <div style="font-size: 3rem; margin-bottom: 1rem; opacity: 0.3;">🛒</div>
            <p style="font-weight: 600; margin-bottom: 0.5rem; color: var(--color-text);">Your cart is empty</p>
            <p style="font-size: var(--font-size-sm);">Looks like you haven't added any items yet. Explore our collection!</p>
          </div>`;
        return;
      }

      let html = '';
      cart.items.forEach((item, i) => {
        const lineIndex = i + 1; // Shopify cart API uses 1-based indices
        // Shopify AJAX cart exposes `image` as a raw URL string
        const imgSrc = typeof item.image === 'string'
          ? item.image.replace(/\.(jpg|png|jpeg|webp)(\?.*)?$/i, '_160x.$1')
          : (item.featured_image?.url || '').replace(/\.(jpg|png|jpeg|webp)(\?.*)?$/i, '_160x.$1');
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="cart-item__variant">${this._escapeHtml(item.variant_title)}</p>`
          : '';

        const qtyDisplay = item.quantity;
        // When qty is 1, the minus button becomes a remove (trash) button
        const minusBtnLabel = qtyDisplay === 1
          ? `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="cart-item__trash-icon"><path d="M2 4h10M5 4V3a1 1 0 011-1h2a1 1 0 011 1v1M4 4v8a1 1 0 001 1h4a1 1 0 001-1V4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
          : '−';
        const minusBtnClass = qtyDisplay === 1
          ? 'cart-item__qty-btn cart-item__qty-btn--minus cart-item__qty-btn--remove'
          : 'cart-item__qty-btn cart-item__qty-btn--minus';
        const minusBtnAria = qtyDisplay === 1 ? 'Remove item' : 'Decrease quantity';

        html += `
          <div class="cart-item" data-line="${lineIndex}">
            <a href="${item.url}">
              ${imgSrc ? `<img src="${imgSrc}" alt="${this._escapeHtml(item.product_title)}" class="cart-item__image" width="80" height="80" loading="lazy">` : ''}
            </a>
            <div class="cart-item__details">
              <a href="${item.url}" class="cart-item__title">${this._escapeHtml(item.product_title)}</a>
              ${variantTitle}
              <p class="cart-item__price">${this._formatMoney(item.final_line_price)}</p>
              <div class="cart-item__quantity">
                <button class="${minusBtnClass}" data-cart-qty="${lineIndex}" data-cart-delta="-1" aria-label="${minusBtnAria}">${minusBtnLabel}</button>
                <span class="cart-item__qty-display">${qtyDisplay}</span>
                <button class="cart-item__qty-btn" data-cart-qty="${lineIndex}" data-cart-delta="1" aria-label="Increase quantity">+</button>
              </div>
              <button class="cart-item__remove" data-cart-remove="${lineIndex}">Remove</button>
            </div>
          </div>`;
      });

      this._itemsEl.innerHTML = html;
    },

    /**
     * Build cart footer HTML with totals, coupon, and checkout.
     */
    _renderFooter(cart) {
      if (!this._footerEl) return;

      if (!cart.items || cart.items.length === 0) {
        this._footerEl.innerHTML = '';
        return;
      }

      const totalPrice = this._formatMoney(cart.total_price);
      // Use a data attribute to check if coupon section is enabled
      const couponEnabled = this.drawer?.dataset.couponEnabled === 'true';

      this._footerEl.innerHTML = `
        ${couponEnabled ? `
        <div class="coupon-form">
          <input type="text" class="coupon-form__input" placeholder="Enter coupon code" id="drawer-coupon-input">
          <button class="btn btn-outline btn-sm" id="drawer-coupon-apply">Apply</button>
        </div>
        <div id="drawer-coupon-message" style="font-size: var(--font-size-xs); margin-bottom: var(--space-sm);"></div>
        ` : ''}
        <div class="cart-drawer__subtotal">
          <span>Total</span>
          <span style="color: var(--color-primary); font-size: var(--font-size-lg);">${totalPrice}</span>
        </div>
        <p class="cart-drawer__tax-note">Estimated Tax Calculated at checkout</p>
        <form action="/cart" method="post" style="margin-top: var(--space-md);">
          <button type="submit" name="checkout" class="btn btn-primary btn-lg btn-block">
            Proceed to Checkout
          </button>
        </form>
        <div style="text-align: center; margin-top: var(--space-md);">
          <a href="/cart" class="btn btn-outline btn-sm btn-block" style="justify-content: center;">
            View Full Cart
          </a>
        </div>`;
    },

    /**
     * Handle quantity +/- button clicks.
     * Reads current quantity from cached cart state, computes new value.
     * If qty reaches 0 → smooth fade + collapse animation, then remove.
     * Otherwise → updateItem and re-render immediately.
     * Uses _isUpdating guard to prevent duplicate concurrent requests.
     */
    async _handleQtyChange(lineIndex, delta) {
      if (this._isUpdating) return;
      this._isUpdating = true;

      try {
        const item = this.cart?.items?.[lineIndex - 1];
        if (!item) return;

        const currentQty = item.quantity;
        const newQty = currentQty + delta;

        if (newQty <= 0) {
          // Animate item out (fade + collapse), then remove + re-render
          const itemEl = this._itemsEl?.querySelector(`.cart-item[data-line="${lineIndex}"]`);
          await this._animateRemove(itemEl, lineIndex);
          showToast(`Removed "${item.product_title}" from cart`, 'info');
        } else {
          await this.updateItem(lineIndex, newQty);
        }
      } finally {
        this._isUpdating = false;
        // Refresh the cart page if the user is on it
        this._refreshCartPage();
      }
    },

    /**
     * Animate a cart item out: fade + horizontal slide + height collapse.
     * After the animation completes, calls removeItem → render() to rebuild
     * the drawer with correct state. The remaining items reflow naturally
     * because the collapsed item releases its layout space during the animation.
     */
    async _animateRemove(itemEl, lineIndex) {
      if (!itemEl) {
        await this.removeItem(lineIndex);
        return;
      }

      // Lock dimensions so the item's layout space stays stable during opacity/slide
      const height = itemEl.offsetHeight;
      itemEl.style.overflow = 'hidden';
      itemEl.style.maxHeight = height + 'px';
      itemEl.style.transition =
        'opacity 0.2s ease, transform 0.2s ease, max-height 0.25s ease 0.15s, margin 0.25s ease 0.15s, padding 0.25s ease 0.15s';

      // Force layout before starting transitions
      itemEl.offsetHeight;

      // Fade + slide out
      itemEl.style.opacity = '0';
      itemEl.style.transform = 'translateX(24px)';

      // After the slide fade, collapse height
      setTimeout(() => {
        itemEl.style.maxHeight = '0px';
        itemEl.style.marginTop = '0px';
        itemEl.style.marginBottom = '0px';
        itemEl.style.paddingTop = '0px';
        itemEl.style.paddingBottom = '0px';
        itemEl.style.borderWidth = '0px';
      }, 200);

      // After full animation, remove from backend and re-render
      setTimeout(async () => {
        await this.removeItem(lineIndex);
      }, 500);
    },

    /**
     * Handle remove button click — same animation, then removal.
     * Shows a toast confirmation after the item is successfully removed.
     */
    async _handleRemove(lineIndex, itemEl) {
      if (this._isUpdating) return;
      this._isUpdating = true;

      try {
        const item = this.cart?.items?.[lineIndex - 1];
        const productTitle = item?.product_title || 'Item';

        if (!itemEl) {
          await this.removeItem(lineIndex);
        } else {
          await this._animateRemove(itemEl, lineIndex);
        }
        showToast(`Removed "${productTitle}" from cart`, 'info');
      } finally {
        this._isUpdating = false;
        this._refreshCartPage();
      }
    },

    /**
     * Refresh the full cart page if the user is viewing it,
     * otherwise just update the drawer counts are already handled by render().
     */
    _refreshCartPage() {
      const cartPage = document.querySelector('.cart-page');
      if (cartPage) {
        // Reload the page to reflect updated cart state
        window.location.reload();
      }
    },

    /**
     * Format cents to money string. Falls back to Intl.NumberFormat if Shopify currency not set.
     */
    _formatMoney(cents) {
      return formatPrice(cents);
    },

    /**
     * Escape HTML special characters to prevent XSS.
     */
    _escapeHtml(str) {
      const div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }
  };

  /* ====================================================================
     Filter Module
     ==================================================================== */

  const FilterModule = {
    activeFilters: {},

    init() {
      // Filter option clicks (only for checkbox-style filters with data-filter attr)
      delegate(document.getElementById('filter-sidebar') || document.body, 'click', '.filter-option:not(.filter-option--link)', function () {
        const filterType = this.dataset.filter;
        const filterValue = this.dataset.value;
        FilterModule.toggleFilter(filterType, filterValue, this);
      });

      // Highlight the active zodiac link based on current URL path
      this._highlightActiveZodiac();

      // Clear all filters
      document.getElementById('clear-all')?.addEventListener('click', () => this.clearAll());
    },

    /**
     * Marks the zodiac link as active if the current URL path matches a zodiac collection.
     */
    _highlightActiveZodiac() {
      const path = window.location.pathname.toLowerCase();
      document.querySelectorAll('.filter-option--link').forEach(link => {
        const href = link.getAttribute('href')?.toLowerCase() || '';
        if (href && path.startsWith(href) || path === href) {
          link.classList.add('filter-option--active');
        }
      });
    },

    toggleFilter(type, value, element) {
      if (!this.activeFilters[type]) {
        this.activeFilters[type] = [];
      }

      const index = this.activeFilters[type].indexOf(value);
      if (index > -1) {
        this.activeFilters[type].splice(index, 1);
        element.classList.remove('filter-option--selected');
      } else {
        this.activeFilters[type].push(value);
        element.classList.add('filter-option--selected');
      }

      // Remove empty filter groups
      if (this.activeFilters[type].length === 0) {
        delete this.activeFilters[type];
      }

      this.updateActiveTags();
      this.applyFilters();
    },

    applyFilters() {
      // Build query string from active filters
      const params = new URLSearchParams();
      for (const [type, values] of Object.entries(this.activeFilters)) {
        values.forEach(v => params.append(`filter.${type}`, v));
      }

      const sort = document.getElementById('sort-select')?.value;
      if (sort && sort !== 'featured') {
        params.append('sort_by', sort);
      }

      // Update URL and reload
      const query = params.toString();
      window.location.search = query ? `?${query}` : '';
    },

    updateActiveTags() {
      const container = document.getElementById('active-filters');
      const clearBtn = document.getElementById('clear-all');
      if (!container) return;

      container.innerHTML = '';
      let hasFilters = false;

      for (const [type, values] of Object.entries(this.activeFilters)) {
        values.forEach(value => {
          hasFilters = true;
          const tag = document.createElement('span');
          tag.className = 'active-filter-tag';
          tag.innerHTML = `${value} <span class="active-filter-tag__remove" data-type="${type}" data-value="${value}">&times;</span>`;
          container.appendChild(tag);
        });
      }

      if (clearBtn) clearBtn.style.display = hasFilters ? 'block' : 'none';

      // Remove tag on click
      container.querySelectorAll('.active-filter-tag__remove').forEach(btn => {
        btn.addEventListener('click', () => {
          const type = btn.dataset.type;
          const value = btn.dataset.value;
          this.activeFilters[type] = this.activeFilters[type].filter(v => v !== value);
          if (this.activeFilters[type].length === 0) delete this.activeFilters[type];
          this.updateActiveTags();
          this.applyFilters();
        });
      });
    },

    clearAll() {
      this.activeFilters = {};
      document.querySelectorAll('.filter-option--selected').forEach(el => {
        el.classList.remove('filter-option--selected');
      });
      this.updateActiveTags();
      this.applyFilters();
    }
  };

  /* ====================================================================
     Collection Module (View Toggle & Sort)
     ==================================================================== */

  const CollectionModule = {
    init() {
      // View toggle
      document.querySelectorAll('.view-toggle__btn').forEach(btn => {
        btn.addEventListener('click', () => this.setView(btn));
      });

      // Sort
      document.getElementById('sort-select')?.addEventListener('change', function () {
        FilterModule.applyFilters();
      });
    },

    setView(activeBtn) {
      // Update active button
      document.querySelectorAll('.view-toggle__btn').forEach(b => b.classList.remove('view-toggle__btn--active'));
      activeBtn.classList.add('view-toggle__btn--active');

      const isGrid = activeBtn.getAttribute('onclick')?.includes('grid');
      const grid = document.getElementById('product-grid');
      if (!grid) return;

      // Toggle CSS classes — product-card renders are not modified.
      // CSS handles the visual transformation: .product-list-container restyles .card to horizontal layout.
      if (isGrid) {
        grid.classList.remove('product-list-container');
        grid.classList.add('grid', 'grid-4');
      } else {
        grid.classList.add('product-list-container');
        grid.classList.remove('grid', 'grid-4');
      }

      localStorage.setItem('collection-view', isGrid ? 'grid' : 'list');
    },

    /**
     * Initialize saved view preference
     */
    applySavedView() {
      const saved = localStorage.getItem('collection-view');
      if (saved === 'list') {
        const listBtn = document.querySelector('.view-toggle__btn:last-child');
        if (listBtn) this.setView(listBtn);
      }
    }
  };

  /* ====================================================================
     Product Page Module (Variant Selector, Qty, Accordion, Tabs)
     ==================================================================== */

  const ProductModule = {
    init() {
      // Quantity selector — single event listener using data-qty-delta attribute.
      // Buttons in product form and cart drawer BOTH rely on this delegate.
      delegate(document.body, 'click', '.quantity-selector__btn', function () {
        const input = this.parentElement.querySelector('.quantity-selector__input');
        if (!input) return;
        const deltaAttr = this.getAttribute('data-qty-delta');
        const delta = deltaAttr ? parseInt(deltaAttr) : (this.textContent.trim() === '+' ? 1 : -1);
        const newVal = parseInt(input.value) + delta;
        if (newVal >= 1 && newVal <= 999) {
          input.value = newVal;
          // Dispatch input event so any listeners (e.g. Shopify theme editor) get updated
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });

      // Accordion toggle — handles both tap and click, updates aria-expanded
      delegate(document.body, 'click', '.product-accordion__header', function () {
        const item = this.parentElement;
        const isOpen = item.classList.toggle('product-accordion__item--open');
        this.setAttribute('aria-expanded', isOpen);
      });

      // Tab switching
      delegate(document.body, 'click', '.product-tabs__btn', function () {
        const tabName = this.getAttribute('onclick')?.match(/'([^']+)'/)?.[1];
        if (!tabName) return;
        ProductModule.switchTab(tabName, this);
      });
    },

    switchTab(tabName, btn) {
      document.querySelectorAll('.product-tabs__btn').forEach(b =>
        b.classList.remove('product-tabs__btn--active'));
      document.querySelectorAll('.product-tabs__panel').forEach(p =>
        p.classList.remove('product-tabs__panel--active'));

      if (btn) btn.classList.add('product-tabs__btn--active');
      const panel = document.getElementById(`tab-${tabName}`);
      if (panel) panel.classList.add('product-tabs__panel--active');
    },

    /**
     * Select variant option on PDP
     */
    selectVariant(optionName, value) {
      const container = document.querySelector(`[data-option="${optionName}"]`);
      if (container) container.textContent = value;

      // Update selected visual state
      const parent = document.querySelector(`[data-option="${optionName}"]`).closest('.product-variants');
      if (parent) {
        parent.querySelectorAll('.variant-option').forEach(opt => {
          opt.classList.toggle('variant-option--selected', opt.textContent.trim() === value);
        });
      }

      // In production: find matching variant and update form
      this.updateVariantId(optionName, value);
    },

    updateVariantId(optionName, value) {
      // In production: match selected options to variant ID
      const form = document.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      // Shopify would handle this via variant select dropdown
    }
  };

  /* ====================================================================
     Global Functions (exposed for inline onclick handlers)
     ==================================================================== */

  window.toggleCart = () => CartModule.toggle();
  window.toggleMobileMenu = () => {
    const drawer = document.getElementById('mobile-drawer');
    const backdrop = document.getElementById('mobile-menu-overlay-backdrop');
    const toggle = document.getElementById('mobile-menu-toggle');
    drawer?.classList.toggle('mobile-drawer--open');
    backdrop?.classList.toggle('mobile-drawer__backdrop--visible');
    toggle?.classList.toggle('header__menu-toggle--active');
    document.body.style.overflow = drawer?.classList.contains('mobile-drawer--open') ? 'hidden' : '';
  };

  window.toggleWishlist = () => {
    showToast('Wishlist feature coming soon! ❤', 'info');
  };

  window.buyNow = () => {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'return_to';
      checkoutInput.value = '/checkout';
      form.appendChild(checkoutInput);
      form.submit();
    }
  };

  /**
   * Add to cart from product detail page — AJAX-based, no page reload.
   * Reads the product variant ID from the hidden form input and quantity from the selector.
   * Shows loading spinner and success feedback instantly.
   */
  window.addToCartFromProduct = async () => {
    const form = document.querySelector('form[action*="/cart/add"]');
    const btn = document.querySelector('.btn-add-to-cart');
    if (!form || !btn) return;

    const variantInput = form.querySelector('input[name="id"]');
    const qtyInput = document.getElementById('product-qty');
    if (!variantInput) return;

    const variantId = variantInput.value;
    const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
    const productName = document.querySelector('.product-info__title')?.textContent?.trim() || 'Item';
    const priceText = document.querySelector('.product-info__current-price')?.textContent?.trim() || '';

    // Show loading state
    const textEl = btn.querySelector('.btn-add-to-cart__text');
    const spinnerEl = btn.querySelector('.btn-add-to-cart__spinner');
    btn.classList.add('btn--loading');
    btn.disabled = true;
    if (textEl) textEl.style.visibility = 'hidden';
    if (spinnerEl) spinnerEl.style.display = '';

    try {
      await CartModule.addItem(variantId, quantity, { name: productName, price: priceText });
    } catch (err) {
      showToast('Failed to add item. Please try again.', 'error');
    } finally {
      // Restore button state
      btn.classList.remove('btn--loading');
      btn.disabled = false;
      if (textEl) textEl.style.visibility = '';
      if (spinnerEl) spinnerEl.style.display = 'none';
    }
  };

  window.updateQty = (delta) => {
    const input = document.getElementById('product-qty');
    if (input) {
      const newVal = parseInt(input.value) + delta;
      if (newVal >= 1 && newVal <= 999) input.value = newVal;
    }
  };

  window.selectVariant = (element, optionName, value) => {
    ProductModule.selectVariant(optionName, value);
  };

  window.toggleAccordion = (headerEl) => {
    const item = headerEl.parentElement;
    const isOpen = item.classList.toggle('product-accordion__item--open');
    headerEl.setAttribute('aria-expanded', isOpen);
  };

  /**
   * Product Gallery Module
   * Handles horizontal scroll-snap gallery with dot indicators and thumbnail navigation.
   * Uses native CSS scroll-snap for momentum scrolling and edge bounce on mobile.
   */
  const ProductGallery = {
    scrollContainer: null,
    slides: null,
    thumbs: null,
    dots: null,
    currentIndex: 0,
    _scrollTimeout: null,

    /**
     * Initialize the product gallery
     */
    init() {
      this.scrollContainer = document.getElementById('product-gallery-scroll');
      if (!this.scrollContainer) return;

      this.slides = this.scrollContainer.querySelectorAll('.product-gallery__slide');
      this.thumbs = document.querySelectorAll('#product-gallery-thumbs .product-gallery__thumb');
      this.dots = document.querySelectorAll('#product-gallery-dots .product-gallery__dot');

      if (!this.slides.length) return;

      // Thumbnail click → scroll to slide
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.goToSlide(i));
      });

      // Dot click → scroll to slide
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goToSlide(i));
      });

      // Arrow buttons (desktop)
      var prevArrow = document.getElementById('gallery-arrow-prev');
      var nextArrow = document.getElementById('gallery-arrow-next');
      if (prevArrow) prevArrow.addEventListener('click', () => this.goToSlide(Math.max(0, this.currentIndex - 1)));
      if (nextArrow) nextArrow.addEventListener('click', () => this.goToSlide(Math.min(this.slides.length - 1, this.currentIndex + 1)));

      // Scroll listener → update active dot/thumb
      this.scrollContainer.addEventListener('scroll', () => this._onScroll());

      // Keyboard navigation (left/right arrows when gallery is focused)
      this.scrollContainer.setAttribute('tabindex', '0');
      this.scrollContainer.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goToSlide(Math.max(0, this.currentIndex - 1));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goToSlide(Math.min(this.slides.length - 1, this.currentIndex + 1));
        }
      });
    },

    /**
     * Scroll to a specific slide by index
     * @param {number} index - Zero-based slide index
     */
    goToSlide(index) {
      if (!this.scrollContainer || !this.slides[index]) return;
      const slide = this.slides[index];
      this.scrollContainer.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth'
      });
    },

    /**
     * Handle scroll event — debounce and update active state
     */
    _onScroll() {
      if (this._scrollTimeout) {
        cancelAnimationFrame(this._scrollTimeout);
      }
      // Use rAF for performant scroll handling
      this._scrollTimeout = requestAnimationFrame(() => {
        this._updateActiveIndex();
      });
    },

    /**
     * Determine which slide is currently most visible and update UI
     */
    _updateActiveIndex() {
      if (!this.scrollContainer || !this.slides.length) return;

      const scrollLeft = this.scrollContainer.scrollLeft;
      const slideWidth = this.slides[0].offsetWidth;
      if (!slideWidth) return;

      // Calculate closest slide index
      const index = Math.round(scrollLeft / slideWidth);
      const clampedIndex = Math.max(0, Math.min(this.slides.length - 1, index));

      if (clampedIndex !== this.currentIndex) {
        this.currentIndex = clampedIndex;
        this._updateIndicators(clampedIndex);
      }
    },

    /**
     * Update active state of dot and thumbnail indicators
     * @param {number} index - Currently active slide index
     */
    _updateIndicators(index) {
      // Update dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('product-gallery__dot--active', i === index);
        dot.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });

      // Update thumbnails
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('product-gallery__thumb--active', i === index);
        thumb.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });

      // Update arrow visibility at edges
      var prevArrow = document.getElementById('gallery-arrow-prev');
      var nextArrow = document.getElementById('gallery-arrow-next');
      if (prevArrow) {
        prevArrow.style.visibility = (index <= 0) ? 'hidden' : '';
      }
      if (nextArrow) {
        nextArrow.style.visibility = (index >= this.slides.length - 1) ? 'hidden' : '';
      }
    }
  };

  // Backward compatibility — keep old function working
  window.switchProductImage = (index) => {
    if (typeof ProductGallery !== 'undefined' && ProductGallery.scrollContainer) {
      ProductGallery.goToSlide(index);
    }
  };

  window.switchTab = (tabName) => {
    const btn = document.querySelector(`[onclick*="${tabName}"]`);
    ProductModule.switchTab(tabName, btn);
  };

  window.applyCoupon = () => {
    const code = document.getElementById('coupon-input')?.value?.trim();
    if (!code) {
      showToast('Please enter a coupon code.', 'info');
      return;
    }
    const msg = document.getElementById('coupon-message');
    // In production: apply coupon via Shopify AJAX
    if (msg) {
      msg.textContent = 'Applying coupon...';
      msg.style.color = 'var(--color-info)';
    }
    // Simulate coupon application
    setTimeout(() => {
      if (code === 'ZODIAC10') {
        if (msg) {
          msg.textContent = '✓ 10% discount applied!';
          msg.style.color = 'var(--color-success)';
        }
        showToast('Coupon applied: 10% off!');
      } else {
        if (msg) {
          msg.textContent = 'Invalid coupon code.';
          msg.style.color = 'var(--color-error)';
        }
      }
    }, 500);
  };

  /**
   * Cart drawer & cart page quantity +/- handler.
   * Computes new absolute quantity from delta, handles 0 → removal.
   * For cart page buttons: disables all qty inputs during update, reloads after completion.
   */
  window.updateCartItem = (lineIndex, delta) => {
    if (CartModule._isUpdating) return;
    // Disable all qty-related controls on the cart page during update
    const cartPage = document.querySelector('.cart-page');
    if (cartPage) {
      cartPage.querySelectorAll('.cart-item__qty-btn, .cart-item__qty-input, .cart-item__remove')
        .forEach(el => { el.disabled = true; el.style.pointerEvents = 'none'; el.style.opacity = '0.4'; });
    }
    CartModule._handleQtyChange(lineIndex, delta);
  };

  /**
   * Cart page remove button handler.
   * Animates item out, disables controls during removal, reloads page after completion.
   */
  window.removeCartItem = (lineIndex) => {
    if (CartModule._isUpdating) return;
    const cartPage = document.querySelector('.cart-page');
    const itemEl = cartPage?.querySelector(`.cart-item[data-line="${lineIndex}"]`);
    if (cartPage) {
      cartPage.querySelectorAll('.cart-item__qty-btn, .cart-item__qty-input, .cart-item__remove')
        .forEach(el => { el.disabled = true; el.style.pointerEvents = 'none'; el.style.opacity = '0.4'; });
    }
    CartModule._handleRemove(lineIndex, itemEl);
  };

  /**
   * Cart drawer direct quantity input (used by initial Liquid-rendered input).
   */
  window.updateCartItemQty = (input) => {
    const lineIndex = parseInt(input.dataset.line);
    const qty = parseInt(input.value);
    if (qty > 0) {
      CartModule.updateItem(lineIndex, qty);
    } else {
      CartModule.removeItem(lineIndex);
    }
  };

  window.setView = (viewType) => {
    // Find the matching button
    const btns = document.querySelectorAll('.view-toggle__btn');
    let btn;
    if (viewType === 'grid') btn = btns[0];
    else if (viewType === 'list') btn = btns[1];
    if (btn) CollectionModule.setView(btn);
  };

  window.applySort = () => {
    FilterModule.applyFilters();
  };

  window.toggleFilterSidebar = () => {
    const sidebar = document.getElementById('filter-sidebar');
    const overlay = document.getElementById('filter-overlay');
    const isOpen = sidebar?.classList.toggle('filter-sidebar--open');

    if (overlay) {
      overlay.classList.toggle('filter-overlay--visible', isOpen);
      overlay.setAttribute('aria-hidden', !isOpen);
    }

    if (isOpen) {
      document.body.classList.add('filter-open');
    } else {
      document.body.classList.remove('filter-open');
    }
  };

  window.clearAllFilters = () => {
    FilterModule.clearAll();
  };

  window.updateCartNote = (note) => {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note })
    }).catch(() => {});
  };

  window.cancelOrder = (orderId) => {
    if (confirm('Are you sure you want to cancel this order?')) {
      // In production: cancel via Shopify API
      showToast('Order cancellation requested. We will process it shortly.', 'info');
    }
  };

  /* ====================================================================
     Back to Top Button
     ==================================================================== */

  const BackToTop = {
    init() {
      const btn = document.getElementById('back-to-top');
      if (!btn) return;
      window.addEventListener('scroll', debounce(() => {
        btn.classList.toggle('back-to-top--visible', window.scrollY > 400);
      }, 100));
      btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  /* ====================================================================
     Header Scroll Effect
     ==================================================================== */

  /* ====================================================================
     Smart Header — hide on scroll down, show on scroll up or hover
     - Tracks scroll direction with dead zone threshold
     - Scroll down past threshold → translateY(-100%) hides the top-bar
     - Scroll up (any amount) → immediately shows top-bar
     - When at page top (scrollY < 20px) → always visible, transparent bg
     - Mouse near top 24px of viewport → reveals header
     - Sets main-content padding-top to top-bar height
     ==================================================================== */

  const SmartHeader = {
    ticking: false,
    topBar: null,
    revealZone: null,
    mainContent: null,
    _lastScrollY: 0,
    _scrollDirection: 'up',
    _hidden: false,
    _visibleThreshold: 50,  // px — hide only after scrolling down this far
    _topThreshold: 20,      // px — near page top, always visible
    _hoverArmed: false,

    init() {
      this.topBar = document.getElementById('top-bar');
      this.mainContent = document.getElementById('main-content');
      if (!this.topBar) return;

      // Respect reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.topBar.style.willChange = 'auto';
        return;
      }

      // Create invisible hover reveal zone at top
      this.revealZone = document.createElement('div');
      this.revealZone.className = 'top-bar__reveal-zone';
      document.body.appendChild(this.revealZone);

      // Set initial padding-top on main content
      this._setPadding();

      // Listeners
       window.addEventListener('scroll', this._onScroll.bind(this), { passive: true });
       window.addEventListener('resize', this._onResize.bind(this));
       this.revealZone.addEventListener('mouseenter', this._onHoverEnter.bind(this));
       this.revealZone.addEventListener('mouseleave', this._onHoverLeave.bind(this));

      // Initial state
      this._lastScrollY = window.scrollY;
      this._updateBarState();
    },

    _onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this._processScroll();
        this.ticking = false;
      });
    },

    _processScroll() {
      const currentY = window.scrollY;

      // Handle resize on mobile (address bar show/hide)
      if (this._didResize) {
        this._setPadding();
        this._didResize = false;
      }

      // Determine scroll direction
      if (currentY <= this._topThreshold) {
        // At very top — always visible, no scroll direction check
        this._scrollDirection = 'up';
      } else if (currentY > this._lastScrollY + 3) {
        this._scrollDirection = 'down';
      } else if (currentY < this._lastScrollY - 3) {
        this._scrollDirection = 'up';
      }
      // Within 3px dead zone: maintain previous direction

      this._lastScrollY = currentY;
      this._updateBarState();
    },

    _updateBarState() {
      const currentY = this._lastScrollY;

      if (this._hoverArmed) {
        // Mouse is in reveal zone — always show
        this._show();
      } else if (currentY <= this._topThreshold) {
        // At top of page — show transparent
        this._show();
        this.topBar.classList.remove('top-bar--scrolled');
      } else if (this._scrollDirection === 'up') {
        // Scrolling up — show immediately
        this._show();
        this.topBar.classList.add('top-bar--scrolled');
      } else if (currentY > this._visibleThreshold) {
        // Scrolling down past threshold — hide
        if (!this._hidden) {
          this._hide();
        }
      }
    },

    _show() {
      if (!this._hidden) return;
      this._hidden = false;
      this.topBar.classList.remove('top-bar--hidden');
    },

    _hide() {
      if (this._hidden) return;
      this._hidden = true;
      this.topBar.classList.add('top-bar--hidden');
    },

    _setPadding() {
      if (!this.topBar || !this.mainContent) return;
      this.mainContent.style.paddingTop = this.topBar.offsetHeight + 'px';
    },

    _onResize() {
      this._didResize = true;
      // Defer padding update to next scroll frame
      requestAnimationFrame(() => this._setPadding());
    },

    _onHoverEnter() {
      this._hoverArmed = true;
      if (this._hidden) this._show();
    },

    _onHoverLeave() {
      this._hoverArmed = false;
      // Re-evaluate: if scrolling down, hide again
      if (this._lastScrollY > this._visibleThreshold && this._scrollDirection === 'down') {
        this._hide();
      }
    }
  };

  /* ====================================================================
     Lazy Loading (Intersection Observer)
     ==================================================================== */

  const LazyLoader = {
    init() {
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              img.removeAttribute('data-srcset');
            }
            observer.unobserve(img);
          }
        });
      }, { rootMargin: '200px 0px', threshold: 0.01 });

      document.querySelectorAll('img[loading="lazy"]').forEach(img => observer.observe(img));
    }
  };

  /* ====================================================================
     Animation on Scroll (AOS)
     ==================================================================== */

  const AnimOnScroll = {
    init() {
      if (!('IntersectionObserver' in window)) return;

      // Skip animations for users who prefer reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.querySelectorAll('.scroll-reveal').forEach(el => {
          el.classList.add('scroll-reveal--visible');
        });
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Fade-up elements (hero content handled by HeroParallax, skip those)
            if (entry.target.classList.contains('animate-fade-up')) {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'translateY(0)';
              entry.target.style.willChange = 'auto';
              observer.unobserve(entry.target);
            }
            // Section scroll-reveal blocks
            if (entry.target.classList.contains('scroll-reveal')) {
              entry.target.classList.add('scroll-reveal--visible');
              observer.unobserve(entry.target);
            }
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

      // Observe fade-up elements, but exclude those inside hero (HeroParallax governs them)
      document.querySelectorAll('.animate-fade-up').forEach(el => {
        if (el.closest('.hero__content')) return;
        el.style.opacity = '0';
        el.style.transform = 'translateY(24px)';
        el.style.willChange = 'opacity, transform';
        observer.observe(el);
      });

      // Observe scroll-reveal sections
      document.querySelectorAll('.scroll-reveal').forEach(el => {
        observer.observe(el);
      });
    }
  };

  /* ====================================================================
     Hero Parallax — three-phase scroll-driven cinematic effect
     Phase 1 (frozen): hero sticks to viewport top, full-screen, no change
     Phase 2 (transition): image zooms 1.0→1.25, overlay 0→0.35, text fades
     Phase 3 (done): hero scrolls away, content below revealed via IntersectionObserver
     - Batched reads/writes for 60fps; spacer element provides scroll room
     ==================================================================== */

  const HeroParallax = {
    ticking: false,
    hero: null,
    spacer: null,
    heroBg: null,
    heroOverlay: null,
    heroContent: null,
    _lastProgress: -1,

    init() {
      this.hero = document.querySelector('.hero');
      if (!this.hero) return;

      this.heroBg = this.hero.querySelector('.hero__bg');
      this.heroOverlay = this.hero.querySelector('.hero__overlay');
      this.heroContent = this.hero.querySelector('.hero__content');
      this.spacer = document.querySelector('.hero__spacer');

      // Respect reduced motion — skip all effects
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Only activate if there's an image to zoom
      if (!this.heroBg || !this.spacer) return;

      // Make hero sticky + set spacer height = viewport height for scroll room
      this.hero.classList.add('hero--sticky', 'hero--parallax');
      this._setSpacerHeight();

      // Show hero content text initially (full opacity)
      Array.from(this.heroContent.children).forEach(child => {
        child.style.opacity = '1';
        child.style.transform = 'translateY(0)';
      });

      window.addEventListener('scroll', this._onScroll.bind(this), { passive: true });
      window.addEventListener('resize', this._onResize.bind(this), { passive: true });
      this.update();
    },

    _onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    },

    _onResize() {
      // Recalculate spacer height on resize (orientation change, etc.)
      requestAnimationFrame(() => this._setSpacerHeight());
    },

    _setSpacerHeight() {
      const vh = window.innerHeight;
      const heroHeight = this.hero.offsetHeight;
      // Spacer = viewport height, providing exactly 1 viewport of scroll room
      this.spacer.style.height = Math.round(vh) + 'px';
    },

    update() {
      if (!this.spacer) return;

      // --- Batch all reads ---
      const spacerRect = this.spacer.getBoundingClientRect();
      const vh = window.innerHeight;
      const spacerHeight = this.spacer.offsetHeight;

      // scrollProgress: 0 when spacer fully below viewport (frozen phase),
      //                1 when spacer fully scrolled past (transition complete)
      // spacerRect.top goes from ~vh (spacer entering) to ~vh - spacerHeight (fully scrolled)
      const scrollProgress = Math.max(0, Math.min(1,
        (vh - spacerRect.top) / spacerHeight
      ));

      // Skip repaint if no meaningful change
      if (Math.abs(scrollProgress - this._lastProgress) < 0.001) return;
      this._lastProgress = scrollProgress;

      // --- Batch all writes ---
      // Image zoom: 1.0 → 1.25 (GPU-composited via CSS custom property)
      const zoom = (1 + scrollProgress * 0.25).toFixed(4);
      this.heroBg.style.setProperty('--hero-zoom', zoom);

      // Overlay darkens: 0 → 0.35
      const overlayAlpha = (scrollProgress * 0.35).toFixed(3);
      this.heroOverlay.style.setProperty('--hero-overlay', overlayAlpha);

      // Text fades out + drifts up in lockstep: finishes at 80% of transition
      const fadeEnd = 0.8;
      const textProgress = Math.min(scrollProgress / fadeEnd, 1);
      const opacity = (1 - textProgress).toFixed(3);
      const yOffset = (textProgress * 28).toFixed(1);

      Array.from(this.heroContent.children).forEach(child => {
        child.style.opacity = opacity;
        child.style.transform = `translateY(${yOffset}px)`;
      });
    }
  };

  /* ====================================================================
     Product Rating Aggregator
     Reads all verified customer reviews from localStorage (key: pf_v2),
     computes per-product arithmetic mean rating and count. Populates
     product-card rating slots on the homepage and collection pages.
     Only displays real ratings — never returns fake/placeholder data.
     ==================================================================== */

  const ProductRatingAggregator = {
    /** localStorage key synced with the feedback system in product.liquid */
    _STORAGE_KEY: 'pf_v2',

    /**
     * Get all reviews for a product handle and compute its average rating.
     * @param {string} handle - The product handle (slug)
     * @returns {{ average: number, count: number } | null}
     *   - average: arithmetic mean rounded to 1 decimal
     *   - count: number of verified reviews
     *   - null: no reviews exist for this product
     */
    getProductRating(handle) {
      if (!handle) return null;

      var allData = this._loadAll();
      var ratings = [];

      // Collect valid numerical ratings for this product handle
      for (var key in allData) {
        if (!Object.prototype.hasOwnProperty.call(allData, key)) continue;
        var review = allData[key];
        if (!review || review.product_handle !== handle) continue;

        var r = parseFloat(review.rating);
        // Validation: only include numbers 1-5
        if (!isNaN(r) && r >= 1 && r <= 5) {
          ratings.push(r);
        }
      }

      if (ratings.length === 0) return null;

      // Arithmetic mean with 1-decimal rounding
      var sum = 0;
      for (var i = 0; i < ratings.length; i++) { sum += ratings[i]; }
      var avg = Math.round((sum / ratings.length) * 10) / 10;

      return { average: avg, count: ratings.length };
    },

    /**
     * Bulk aggregation: compute ratings for multiple product handles at once.
     * Efficient single-pass over localStorage data — O(n) where n = total reviews.
     * @param {string[]} handles - Array of product handles to look up
     * @returns {Object.<string, {average: number, count: number}>} Map of handle → rating data
     */
    getBulkRatings(handles) {
      var result = {};
      if (!handles || !handles.length) return result;

      var handleSet = {};
      for (var i = 0; i < handles.length; i++) { handleSet[handles[i]] = true; }

      // Initialize buckets
      var buckets = {};
      for (var h in handleSet) {
        if (Object.prototype.hasOwnProperty.call(handleSet, h)) {
          buckets[h] = [];
        }
      }

      // Single pass: collect ratings into per-handle buckets
      var allData = this._loadAll();
      for (var key in allData) {
        if (!Object.prototype.hasOwnProperty.call(allData, key)) continue;
        var review = allData[key];
        if (!review || !review.product_handle) continue;

        var bucket = buckets[review.product_handle];
        if (!bucket) continue; // not a handle we care about

        var r = parseFloat(review.rating);
        if (!isNaN(r) && r >= 1 && r <= 5) {
          bucket.push(r);
        }
      }

      // Compute averages
      for (var h in buckets) {
        if (!Object.prototype.hasOwnProperty.call(buckets, h)) continue;
        var ratingsArr = buckets[h];
        if (ratingsArr.length === 0) continue;

        var sum = 0;
        for (var j = 0; j < ratingsArr.length; j++) { sum += ratingsArr[j]; }
        result[h] = {
          average: Math.round((sum / ratingsArr.length) * 10) / 10,
          count: ratingsArr.length
        };
      }

      return result;
    },

    /**
     * Build HTML for a star rating display.
     * @param {number} rating - Average rating value (0-5)
     * @param {number} count - Number of reviews
     * @returns {string} HTML string
     */
    _buildRatingHTML(rating, count) {
      var stars = '';
      for (var i = 1; i <= 5; i++) {
        if (i <= Math.floor(rating)) {
          stars += '★';
        } else if (i === Math.floor(rating) + 1 && rating % 1 >= 0.25 && rating % 1 <= 0.75) {
          stars += '<span style="opacity:0.5;">★</span>';
        } else {
          stars += '<span style="opacity:0.2;">★</span>';
        }
      }
      var countStr = count > 0 ? ' <span style=\"color:var(--color-text-muted);margin-left:4px;\">(' + count + ')</span>' : '';
      return stars + countStr;
    },

    /**
     * Scan the DOM for product-card rating slots and populate them
     * with computed real ratings from customer feedback data.
     */
    init() {
      var cards = document.querySelectorAll('.card[data-product-handle]');
      if (!cards.length) return;

      // Collect unique handles on this page
      var handles = [];
      for (var i = 0; i < cards.length; i++) {
        var h = cards[i].getAttribute('data-product-handle');
        if (h && handles.indexOf(h) === -1) handles.push(h);
      }

      // Bulk-compute ratings in a single pass
      var ratings = this.getBulkRatings(handles);

      // Populate each card's rating slot
      for (var j = 0; j < cards.length; j++) {
        var card = cards[j];
        var handle = card.getAttribute('data-product-handle');
        if (!handle || !ratings[handle]) continue;

        var ratingEl = card.querySelector('.card__rating');
        if (!ratingEl) continue;

        var data = ratings[handle];
        ratingEl.innerHTML = this._buildRatingHTML(data.average, data.count);
        ratingEl.style.display = '';
        ratingEl.removeAttribute('aria-hidden');
      }
    },

    /** Load all review data from localStorage */
    _loadAll() {
      try {
        var raw = localStorage.getItem(this._STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
      } catch (e) { return {}; }
    }
  };

  /* ====================================================================
     Page Transition Observer (smooth page transitions)
     ==================================================================== */

  const PageTransition = {
    init() {
      // Add entrance animation to main content
      const main = document.getElementById('main-content');
      if (main) {
        main.style.opacity = '0';
        main.style.transform = 'translateY(8px)';
        main.style.transition = 'opacity 350ms cubic-bezier(0.4,0,0.2,1), transform 350ms cubic-bezier(0.4,0,0.2,1)';
        requestAnimationFrame(() => {
          main.style.opacity = '1';
          main.style.transform = 'translateY(0)';
        });
      }
    }
  };

  /* ====================================================================
     Collection Lineup — horizontally scrollable homepage collection cards
     ==================================================================== */

  const CollectionLineup = {
    init() {
      const lineup = document.getElementById('collection-lineup');
      if (!lineup) return;

      const viewport = document.getElementById('collection-lineup-viewport');
      const track = document.getElementById('collection-lineup-track');
      const prevBtn = lineup.querySelector('.collection-lineup__nav-btn--prev');
      const nextBtn = lineup.querySelector('.collection-lineup__nav-btn--next');
      const cards = track ? track.querySelectorAll('.collection-lineup__card') : [];
      if (!viewport || !track || cards.length === 0) return;

      function getScrollAmount() {
        const cardEl = cards[0];
        if (!cardEl) return 260;
        const style = window.getComputedStyle(track);
        const gap = parseInt(style.columnGap || style.gap || 0);
        return cardEl.offsetWidth + (isNaN(gap) ? 0 : gap);
      }

      function updateArrows() {
        if (!prevBtn || !nextBtn) return;
        const atStart = viewport.scrollLeft <= 2;
        const atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;

        if (atStart) prevBtn.classList.add('collection-lineup__nav-btn--hidden');
        else prevBtn.classList.remove('collection-lineup__nav-btn--hidden');

        if (atEnd) nextBtn.classList.add('collection-lineup__nav-btn--hidden');
        else nextBtn.classList.remove('collection-lineup__nav-btn--hidden');
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          viewport.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          viewport.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
        });
      }

      viewport.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows, { passive: true });
      updateArrows();

      // If track fits entirely, hide both arrows
      if (track.scrollWidth <= viewport.clientWidth + 4) {
        if (prevBtn) prevBtn.classList.add('collection-lineup__nav-btn--hidden');
        if (nextBtn) nextBtn.classList.add('collection-lineup__nav-btn--hidden');
      }
    }
  };

  /* ====================================================================
     Zodiac Carousel — 12 zodiac signs with infinite scroll
     ==================================================================== */

  const ZodiacCarousel = {
    init() {
      const carousel = document.getElementById('zodiac-carousel');
      if (!carousel) return;

      const viewport = document.getElementById('zodiac-carousel-viewport');
      const track = document.getElementById('zodiac-carousel-track');
      const prevBtn = carousel.querySelector('.zodiac-carousel__nav-btn--prev');
      const nextBtn = carousel.querySelector('.zodiac-carousel__nav-btn--next');
      if (!viewport || !track) return;

      const originalCards = track.querySelectorAll('.zodiac-carousel__card');
      if (originalCards.length === 0) return;

      // Clone all 12 cards once for seamless looping (24 total)
      originalCards.forEach(card => {
        track.appendChild(card.cloneNode(true));
      });

      let setWidth = 0;
      let cardScrollAmount = 0;

      function recalculate() {
        const card = originalCards[0];
        if (!card) return;
        const style = window.getComputedStyle(track);
        const gap = parseInt(style.columnGap || style.gap || 0) || 0;
        cardScrollAmount = card.offsetWidth + gap;
        setWidth = cardScrollAmount * originalCards.length;
      }
      recalculate();
      window.addEventListener('resize', recalculate);

      // Nav buttons: pre-wrap target so smooth scroll never crosses boundary
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          let target = viewport.scrollLeft - cardScrollAmount;
          while (target < 0) target += setWidth;
          viewport.scrollTo({ left: target, behavior: 'smooth' });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          let target = viewport.scrollLeft + cardScrollAmount;
          while (target >= setWidth) target -= setWidth;
          viewport.scrollTo({ left: target, behavior: 'smooth' });
        });
      }

      // Scroll handler for manual scrolling — modulo wrap with cooldown guard
      let cooldown = false;
      viewport.addEventListener('scroll', () => {
        if (cooldown || setWidth <= 0) return;

        const sl = viewport.scrollLeft;
        if (sl >= setWidth || sl < 0) {
          cooldown = true;
          // Modulo ensures correct wrap even after large overscrolls
          viewport.scrollLeft = ((sl % setWidth) + setWidth) % setWidth;
          // Cooldown prevents oscillation from residual smooth-scroll events
          setTimeout(() => { cooldown = false; }, 150);
        }
      }, { passive: true });
    }
  };

  /* ====================================================================
     Zodiac Donut Module — interactive donut chart with info panel
     ==================================================================== */

  const ZodiacDonut = {
    segments: null,
    infoDefault: null,
    infoDetails: null,
    activeIndex: -1,

    init() {
      this.segments = document.querySelectorAll('.zodiac-seg');
      if (!this.segments.length) return;

      this.infoDefault = document.getElementById('zodiac-info-default');
      this.infoDetails = [];
      for (let i = 0; i < 12; i++) {
        this.infoDetails[i] = document.getElementById('zodiac-info-' + i);
      }

      this.segments.forEach(seg => {
        // Click to toggle active state — stopPropagation prevents document handler from interfering
        seg.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._toggleDetail(parseInt(seg.dataset.index));
        });
      });

      // Click outside to deselect
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.zodiac-seg') && !e.target.closest('.zodiac-info')) {
          this._hideDetail();
        }
      });
    },

    _toggleDetail(index) {
      if (this.activeIndex === index) {
        this._hideDetail();
        return;
      }
      this._showDetail(index);
    },

    _showDetail(index) {
      // Show the selected detail panel, hide all others + default panel
      var prevIndex = this.activeIndex;
      this.activeIndex = index;

      // Hide default panel
      if (this.infoDefault) {
        this.infoDefault.classList.add('is-hidden');
      }

      // Deactivate previous detail
      if (prevIndex !== -1) {
        var prev = this.infoDetails[prevIndex];
        if (prev) prev.classList.remove('is-active');
      }

      // Activate new detail
      var newPanel = this.infoDetails[index];
      if (newPanel) newPanel.classList.add('is-active');

      // Update segment highlights
      this.segments.forEach(function(s) { s.classList.remove('zodiac-seg--active'); });
      var activeSeg = this.segments[index];
      if (activeSeg) activeSeg.classList.add('zodiac-seg--active');
    },

    _hideDetail() {
      if (this.activeIndex === -1) return;

      var prevIndex = this.activeIndex;
      this.activeIndex = -1;

      // Hide current detail
      var prev = this.infoDetails[prevIndex];
      if (prev) prev.classList.remove('is-active');

      // Show default panel
      if (this.infoDefault) {
        this.infoDefault.classList.remove('is-hidden');
      }

      // Clear segment highlights
      this.segments.forEach(function(s) { s.classList.remove('zodiac-seg--active'); });
    }
  };

  /* ====================================================================
     SEO & Analytics Helpers
     ==================================================================== */

  const AnalyticsHelper = {
    init() {
      // Track search queries
      const searchForm = document.querySelector('.header__search-form');
      searchForm?.addEventListener('submit', function () {
        const query = this.querySelector('input[name="q"]')?.value;
        if (query && typeof gtag !== 'undefined') {
          gtag('event', 'search', { search_term: query });
        }
      });
    }
  };

  /* ====================================================================
     Story Banner — page-blocking door-opening animation for Our Story page
     - Locks body overflow on page load → no scrolling until animation completes
     - Doors part to 30vw each side (stay visible as stage curtains)
     - Story text layer fades in + scales up at 40% of door progress
     - On completion: unlocks body overflow, collapses spacer, restores scrolling
     - Reduced-motion: skips animation, unblocks immediately
     ==================================================================== */

  const StoryBanner = {
    banner: null,
    spacer: null,
    storyText: null,
    _bodyEl: null,
    _hasTriggered: false,
    _scrollbarWidth: 0,

    init() {
      this.banner = document.getElementById('story-banner');
      if (!this.banner) return;

      this.storyText = document.getElementById('story-banner-text');
      this.spacer = this.banner.nextElementSibling;
      this._bodyEl = document.body;

      // Reduced motion: skip animation, unblock page immediately
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this._unblockPage();
        return;
      }

      const duration = parseFloat(this.banner.dataset.duration || '1.2');
      const delay = parseFloat(this.banner.dataset.delay || '0.4');

      // Set CSS custom properties for door animation timing
      this.banner.style.setProperty('--story-door-duration', duration + 's');
      this.banner.style.setProperty('--story-door-easing', 'cubic-bezier(0.65, 0, 0.35, 1)');

      // Handle image loading states
      this._handleImageLoad();

      // Block page scrolling immediately — prevent any interaction until animation completes
      this._blockPage();

      // Auto-fire animation after configurable delay
      setTimeout(() => {
        this._fire(duration);
      }, delay * 1000);
    },

    /**
     * Blocks page scrolling. Calculates scrollbar width to prevent layout shift
     * when hiding overflow (body width jumps when scrollbar disappears).
     */
    _blockPage() {
      if (this._hasTriggered) return;
      this._scrollbarWidth = window.innerWidth - this._bodyEl.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', this._scrollbarWidth + 'px');
      this._bodyEl.classList.add('story-banner--blocked');
    },

    /**
     * Unblocks page scrolling. Removes the overflow lock, allowing the user
     * to scroll and interact with the page normally.
     */
    _unblockPage() {
      this._bodyEl.classList.remove('story-banner--blocked');
      document.documentElement.style.removeProperty('--scrollbar-width');
    },

    /**
     * Hides loading skeleton once background image has finished loading.
     */
    _handleImageLoad() {
      const bgImage = this.banner.querySelector('.story-banner__bg');
      if (bgImage) {
        if (bgImage.complete) {
          this.banner.classList.add('story-banner--loaded');
        } else {
          bgImage.addEventListener('load', () => {
            this.banner.classList.add('story-banner--loaded');
          });
          bgImage.addEventListener('error', () => {
            this.banner.classList.add('story-banner--loaded');
          });
        }
      } else {
        this.banner.classList.add('story-banner--loaded');
      }
    },

    _fire(duration) {
      if (this._hasTriggered) return;
      this._hasTriggered = true;

      const durMs = duration * 1000;

      // Phase 1: doors begin parting (CSS transition runs for full `duration` seconds)
      this.banner.classList.add('story-banner--open');

      // Phase 2: story text fades in + scales up at 25% of door animation
      setTimeout(() => {
        this.banner.classList.add('story-banner--reveal');
      }, durMs * 0.25);

      // Phase 3: doors fully open. Transition banner into flow + unblock page.
      setTimeout(() => {
        // Batch all layout changes in one rAF to avoid visible intermediate states.
        // Spacer collapse is handled by CSS (.story-banner--in-flow + .story-banner__spacer)
        // so it runs in the same style pass as the class change — no height-delta flash.
        requestAnimationFrame(() => {
          this.banner.classList.remove('story-banner--blocking');
          this.banner.classList.add('story-banner--in-flow');
          this._unblockPage();
        });
      }, durMs + 600);
    }
  };

  /* ====================================================================
     Boot
     ==================================================================== */

  document.addEventListener('DOMContentLoaded', () => {
    CartModule.init();
    FilterModule.init();
    CollectionModule.init();
    ProductModule.init();
    BackToTop.init();
    SmartHeader.init();
    HeroParallax.init();
    StoryBanner.init();
    LazyLoader.init();
    AnimOnScroll.init();
    PageTransition.init();
    AnalyticsHelper.init();
    ZodiacDonut.init();
    ZodiacCarousel.init();
    CollectionLineup.init();
    ProductGallery.init();
    ProductRatingAggregator.init();

    // Apply saved collection view preference
    CollectionModule.applySavedView();
  });

})();
